import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageCircle, User, Image, X, Paperclip, Download, ChevronDown, UserCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const MessageBoard = ({ caseId, messagesAPI }) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);
  const fileInputRef = useRef(null);
  
  // Admin selection state (for clients)
  const [admins, setAdmins] = useState([]);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  
  // Attachment state
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  
  // Image preview modal
  const [previewImage, setPreviewImage] = useState(null);

  useEffect(() => {
    loadMessages();
    if (user?.role === 'client') {
      loadAdmins();
    }
    // Poll for new messages every 10 seconds
    const interval = setInterval(loadMessages, 10000);
    return () => clearInterval(interval);
  }, [caseId]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadMessages = async () => {
    try {
      const response = await messagesAPI.getMessages(caseId);
      setMessages(response.data);
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadAdmins = async () => {
    try {
      const response = await api.get('/messages/admins');
      setAdmins(response.data);
      // Auto-select first admin if none selected
      if (response.data.length > 0 && !selectedAdmin) {
        setSelectedAdmin(response.data[0]);
      }
    } catch (error) {
      console.error('Failed to load admins:', error);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    // Limit to 3 attachments
    if (attachments.length + files.length > 3) {
      toast.error('Maximum 3 attachments allowed per message');
      return;
    }
    
    setUploading(true);
    
    for (const file of files) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name} is not an image file`);
        continue;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error(`${file.name} is too large (max 5MB)`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post('/messages/attachment', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        setAttachments(prev => [...prev, response.data]);
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    
    setUploading(false);
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const removeAttachment = (attachmentId) => {
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() && attachments.length === 0) return;

    setSending(true);
    try {
      const messageData = {
        case_id: caseId,
        content: newMessage.trim() || (attachments.length > 0 ? '[Screenshot attached]' : ''),
        attachments: attachments.length > 0 ? attachments : null
      };
      
      // Add recipient if client selected an admin
      if (user?.role === 'client' && selectedAdmin) {
        messageData.recipient_id = selectedAdmin.id;
      }
      
      await messagesAPI.createMessage(messageData);
      setNewMessage('');
      setAttachments([]);
      await loadMessages();
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const isOwnMessage = (message) => message.sender_id === user.id;
  
  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || '';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[600px] bg-white rounded-lg border border-slate-200">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
            <MessageCircle className="text-purple-600" size={20} />
          </div>
          <div>
            <h3 className="font-bold text-slate-900">Case Communication</h3>
            <p className="text-xs text-slate-600">
              {user?.role === 'client' 
                ? selectedAdmin 
                  ? `Messaging: ${selectedAdmin.full_name}` 
                  : 'Select an admin to message'
                : 'Chat with your client'}
            </p>
          </div>
        </div>
        
        {/* Admin Selector (for clients only) */}
        {user?.role === 'client' && admins.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAdminDropdown(!showAdminDropdown)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
            >
              <UserCircle2 size={18} className="text-purple-600" />
              <span className="text-sm font-medium text-slate-700">
                {selectedAdmin ? selectedAdmin.full_name : 'Select Admin'}
              </span>
              <ChevronDown size={16} className={`text-slate-400 transition-transform ${showAdminDropdown ? 'rotate-180' : ''}`} />
            </button>
            
            {showAdminDropdown && (
              <div className="absolute right-0 mt-1 w-64 bg-white border border-slate-200 rounded-lg shadow-lg z-10">
                <div className="p-2 border-b border-slate-100">
                  <p className="text-xs text-slate-500 font-medium">Select Admin to Message</p>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  {admins.map((admin) => (
                    <button
                      key={admin.id}
                      type="button"
                      onClick={() => {
                        setSelectedAdmin(admin);
                        setShowAdminDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 hover:bg-purple-50 transition-colors flex items-center gap-2 ${
                        selectedAdmin?.id === admin.id ? 'bg-purple-50' : ''
                      }`}
                    >
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        admin.role === 'super_admin' ? 'bg-red-100' : 'bg-purple-100'
                      }`}>
                        <User size={14} className={admin.role === 'super_admin' ? 'text-red-600' : 'text-purple-600'} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-slate-900 truncate">{admin.full_name}</p>
                        <p className="text-xs text-slate-500 truncate">{admin.email}</p>
                      </div>
                      {selectedAdmin?.id === admin.id && (
                        <div className="w-2 h-2 rounded-full bg-purple-600"></div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center">
            <MessageCircle className="text-slate-300 mb-3" size={48} />
            <p className="text-slate-500">No messages yet</p>
            <p className="text-sm text-slate-400">Start the conversation!</p>
          </div>
        ) : (
          messages.map((message) => (
            <div
              key={message.id}
              className={`flex gap-3 ${isOwnMessage(message) ? 'flex-row-reverse' : 'flex-row'}`}
            >
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                message.sender_role === 'admin' || message.sender_role === 'super_admin' ? 'bg-purple-100' : 'bg-blue-100'
              }`}>
                <User size={16} className={message.sender_role === 'admin' || message.sender_role === 'super_admin' ? 'text-purple-600' : 'text-blue-600'} />
              </div>

              {/* Message Bubble */}
              <div className={`flex flex-col max-w-[70%] ${isOwnMessage(message) ? 'items-end' : 'items-start'}`}>
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-xs font-semibold text-slate-700">{message.sender_name}</span>
                  {message.recipient_name && (
                    <span className="text-xs text-purple-600">→ {message.recipient_name}</span>
                  )}
                  <span className="text-xs text-slate-400">
                    {format(new Date(message.created_at), 'MMM d, h:mm a')}
                  </span>
                </div>
                <div
                  className={`rounded-lg px-4 py-2 ${
                    isOwnMessage(message)
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
                  
                  {/* Attachments */}
                  {message.attachments && message.attachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {message.attachments.map((att, idx) => (
                        <div key={att.id || idx} className="relative group">
                          <img
                            src={`${getBackendUrl()}${att.file_url}`}
                            alt={att.filename}
                            className="max-w-full max-h-48 rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setPreviewImage(`${getBackendUrl()}${att.file_url}`)}
                          />
                          <div className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <a
                              href={`${getBackendUrl()}${att.file_url}`}
                              download={att.filename}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-1 bg-black/50 rounded text-white hover:bg-black/70"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Download size={14} />
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Attachments Preview */}
      {attachments.length > 0 && (
        <div className="px-4 py-2 bg-slate-50 border-t border-slate-200">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-slate-500">Attachments:</span>
            {attachments.map((att) => (
              <div key={att.id} className="relative group">
                <img
                  src={`${getBackendUrl()}${att.file_url}`}
                  alt={att.filename}
                  className="w-16 h-16 object-cover rounded-lg border border-slate-200"
                />
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <X size={12} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Input Area */}
      <form onSubmit={handleSendMessage} className="p-4 border-t border-slate-200 bg-slate-50">
        <div className="flex gap-2 items-end">
          {/* File upload button */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading || attachments.length >= 3}
            className="p-2 text-slate-500 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="Attach screenshot (max 3 images)"
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600"></div>
            ) : (
              <Image size={20} />
            )}
          </button>
          
          <input
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type your message..."
            className="flex-1 px-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={sending || (!newMessage.trim() && attachments.length === 0)}
            className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
          >
            <Send size={18} />
            <span className="hidden sm:inline">Send</span>
          </button>
        </div>
        <p className="text-xs text-slate-400 mt-1">
          {attachments.length > 0 ? `${attachments.length}/3 screenshots attached` : 'Click the image icon to attach screenshots'}
        </p>
      </form>

      {/* Image Preview Modal */}
      {previewImage && (
        <div 
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setPreviewImage(null)}
        >
          <div className="relative max-w-4xl max-h-[90vh]">
            <img
              src={previewImage}
              alt="Preview"
              className="max-w-full max-h-[90vh] object-contain rounded-lg"
            />
            <button
              onClick={() => setPreviewImage(null)}
              className="absolute top-2 right-2 p-2 bg-black/50 text-white rounded-full hover:bg-black/70 transition-colors"
            >
              <X size={20} />
            </button>
            <a
              href={previewImage}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="absolute bottom-2 right-2 px-3 py-1 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-colors flex items-center gap-2 text-sm"
              onClick={(e) => e.stopPropagation()}
            >
              <Download size={16} />
              Download
            </a>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageBoard;
